import React from 'react';
import { Box, Paper, Divider, Typography, CircularProgress, Tooltip } from '@mui/material';
import { UserButton } from '@clerk/clerk-react';

interface TopRightControlsProps {
  quotaCount: number;
  onOpenQuota: () => void;
  onOpenCompositeBuilder?: () => void;
  onCheckCompliance?: () => void;
  complianceScore?: number | null;
  isCheckingCompliance?: boolean;
}

const CardIcon = () => {
  return (
    <img 
      src="/images/credit-card.svg" 
      alt="Credit Card"
      style={{ width: '1em', height: '1em' }}
    />
  );
};

export const TopRightControls: React.FC<TopRightControlsProps> = ({
  quotaCount,
  onOpenQuota,
  onOpenCompositeBuilder,
  onCheckCompliance,
  complianceScore,
  isCheckingCompliance = false,
}) => {
  // Determine display value and color based on compliance score
  const displayScore = complianceScore !== null && complianceScore !== undefined ? complianceScore : quotaCount;
  const scoreColor = complianceScore !== null && complianceScore !== undefined
    ? (complianceScore >= 80 ? '#4caf50' : complianceScore >= 60 ? '#ff9800' : '#f44336')
    : '#C62828';
  
  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        right: 32,
        top: 32,
        zIndex: 20,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        height: 48,
        px: 1,
        gap: 0.5,
        borderRadius: 999,
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 18px 40px rgba(15, 23, 42, 0.22)',
      }}
    >
      {/* Compliance Tracker */}
      <Tooltip title={complianceScore !== null ? `Compliance: ${complianceScore}% (Click to refresh)` : "Check Compliance"} placement="bottom">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isCheckingCompliance ? 'wait' : 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(198, 40, 40, 0.08)',
            },
          }}
          onClick={onCheckCompliance}
        >
          <Box
            sx={{
              position: 'relative',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isCheckingCompliance ? (
              <CircularProgress
                size={24}
                thickness={4}
                sx={{ color: scoreColor }}
              />
            ) : (
              <>
                <CircularProgress
                  variant="determinate"
                  value={displayScore}
                  thickness={4}
                  size={28}
                  sx={{
                    color: scoreColor,
                    position: 'absolute',
                    left: 0,
                    top: 0,
                  }}
                />
                <Typography
                  sx={{
                    color: scoreColor,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    lineHeight: 1,
                  }}
                >
                  {complianceScore !== null && complianceScore !== undefined ? displayScore : '0'}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Tooltip>

      {/* Composite Builder */}
      {onOpenCompositeBuilder && (
        <Tooltip title="Create Multi-Message Board" placement="bottom">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
              },
            }}
            onClick={onOpenCompositeBuilder}
          >
            <Typography sx={{ fontSize: '1.25rem' }}>🎨</Typography>
          </Box>
        </Tooltip>
      )}

      {/* Divider */}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: 'center' }} />

      {/* User Button */}
      <Box
        sx={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <UserButton
          afterSignOutUrl="/"
          appearance={{
            elements: {
              avatarBox: {
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: '#F8F8F8',
              },
              userButtonBox: {
                padding: 0,
                boxShadow: 'none',
                border: 'none',
                background: 'transparent',
              },
            },
          }}
        >
          <UserButton.UserProfileLink 
            label="Billing" 
            url="https://billing.stripe.com/p/login/bJe28r4YJ4oXbl71ITak000" 
            labelIcon={<CardIcon />}
          />
        </UserButton>
      </Box>
    </Paper>
  );
};

// Export with both names for backward compatibility
export const BottomLeftControls = TopRightControls;
export default TopRightControls;
